import { useState } from "react";
import { Check, Loader2, Trash2, Trophy, UserPlus } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Panel } from "@/components/community/kit";
import { when } from "@/lib/community";

const TONE = {
  nominations: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  voting: "border-gold/40 bg-gold/10 text-gold",
  closed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  draft: "",
};

export const ElectionCard = ({ election, communityId, roster, canManage, myUserId, onChanged }) => {
  const [ballot, setBallot] = useState({});
  const [statement, setStatement] = useState("");
  const [standing, setStanding] = useState("");
  const [nominee, setNominee] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const run = async (fn, key) => {
    setBusy(key); setError("");
    try { await fn(); onChanged?.(); } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const stand = (seat) => run(async () => {
    await api.post(`/communities/${communityId}/elections/${election.id}/nominate`,
      { seat, statement });
    setStatement(""); setStanding("");
  }, `stand-${seat}`);

  const nominate = (seat) => run(async () => {
    await api.post(`/communities/${communityId}/elections/${election.id}/nominate`,
      { seat, user_id: nominee[seat] });
    setNominee({ ...nominee, [seat]: "" });
  }, `nom-${seat}`);

  const respond = (candidateId, accept) => run(async () => {
    await api.post(
      `/communities/${communityId}/elections/${election.id}/candidates/${candidateId}`,
      { accept, statement });
    setStatement("");
  }, `resp-${candidateId}`);

  const vote = () => run(async () => {
    await api.post(`/communities/${communityId}/elections/${election.id}/vote`, { choices: ballot });
  }, "vote");

  const closed = election.status === "closed";
  const votable = election.can_vote;

  return (
    <Panel testId={`election-${election.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-base text-slate-100 md:text-lg">{election.title}</h3>
          <p className="mt-1 flex flex-wrap items-center gap-2">
            <Chip testId={`election-status-${election.id}`} className={TONE[election.status]}>
              {election.status_label}
            </Chip>
            <Chip>{election.ballots} ballots cast</Chip>
            {election.dues_required && <Chip>dues must be current</Chip>}
            {election.voting_closes && !closed && (
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                closes {when(election.voting_closes)}
              </span>
            )}
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            {election.status === "nominations" && (
              <Button data-testid={`election-open-voting-${election.id}`} size="sm"
                onClick={() => run(async () => {
                  await api.post(`/communities/${communityId}/elections/${election.id}/status`,
                    { status: "voting" });
                }, "open")}>
                Open voting
              </Button>
            )}
            {election.status === "voting" && (
              <Button data-testid={`election-close-${election.id}`} size="sm"
                onClick={() => run(async () => {
                  await api.post(`/communities/${communityId}/elections/${election.id}/close`);
                }, "close")}>
                Count &amp; declare
              </Button>
            )}
            <Button data-testid={`election-delete-${election.id}`} size="sm" variant="ghost"
              className="text-slate-400 hover:text-red-300"
              onClick={() => run(async () => {
                await api.delete(`/communities/${communityId}/elections/${election.id}`);
              }, "del")}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {election.description && (
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-400">
          {election.description}
        </p>
      )}
      {error && <p data-testid={`election-error-${election.id}`} className="mt-3 text-sm text-red-300">{error}</p>}
      {election.why_not && (
        <p data-testid={`election-why-not-${election.id}`} className="mt-3 text-xs text-amber-300">
          {election.why_not}
        </p>
      )}

      <div className="mt-4 space-y-4">
        {election.seats.map((seat) => (
          <div key={seat.key} data-testid={`election-seat-${election.id}-${seat.key}`}
            className="rounded-xl border border-ink-600 bg-ink-900/60 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-slate-100">{seat.title}</p>
              {seat.label && <Chip>{seat.label} role</Chip>}
            </div>

            {seat.candidates.length === 0 ? (
              <p className="mt-2 text-xs text-slate-500">Nobody standing yet.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {seat.candidates.map((c) => (
                  <li key={c.id} data-testid={`candidate-${c.id}`}
                    className={`rounded-lg border p-3 ${
                      closed && seat.winner === c.name ? "border-gold/40 bg-gold/5" : "border-ink-700 bg-ink-800"}`}>
                    <label className="flex cursor-pointer items-start gap-3">
                      {votable && (
                        <input data-testid={`ballot-${seat.key}-${c.id}`} type="radio"
                          name={`${election.id}-${seat.key}`} className="mt-1 h-4 w-4"
                          checked={ballot[seat.key] === c.id}
                          onChange={() => setBallot({ ...ballot, [seat.key]: c.id })} />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-center gap-2 text-sm text-slate-100">
                          {c.name}
                          {closed && seat.winner === c.name && (
                            <Chip className="border-gold/40 bg-gold/10 text-gold">
                              <Trophy className="mr-1 h-3 w-3" /> elected
                            </Chip>
                          )}
                          {closed && (
                            <span data-testid={`candidate-votes-${c.id}`}
                              className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                              {c.votes} votes
                            </span>
                          )}
                        </p>
                        {c.statement && (
                          <p className="mt-1 whitespace-pre-wrap text-xs text-slate-400">{c.statement}</p>
                        )}
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            )}

            {seat.pending.filter((p) => p.user_id === myUserId).map((p) => (
              <div key={p.id} data-testid={`nomination-${p.id}`}
                className="mt-3 rounded-lg border border-gold/30 bg-gold/5 p-3">
                <p className="text-xs text-gold">
                  {p.nominated_by || "A member"} nominated you for {seat.title}.
                </p>
                <Textarea data-testid={`nomination-statement-${p.id}`} rows={2}
                  className="mt-2" placeholder="Why members should pick you (optional)"
                  value={statement} onChange={(e) => setStatement(e.target.value)} />
                <div className="mt-2 flex gap-2">
                  <Button data-testid={`nomination-accept-${p.id}`} size="sm"
                    onClick={() => respond(p.id, true)} disabled={busy === `resp-${p.id}`}>
                    <Check className="mr-1.5 h-3.5 w-3.5" /> Accept
                  </Button>
                  <Button data-testid={`nomination-decline-${p.id}`} size="sm" variant="outline"
                    onClick={() => respond(p.id, false)} disabled={busy === `resp-${p.id}`}>
                    Decline
                  </Button>
                </div>
              </div>
            ))}

            {election.status === "nominations" && (
              <div className="mt-3 space-y-2">
                {standing === seat.key ? (
                  <>
                    <Textarea data-testid={`stand-statement-${seat.key}`} rows={2}
                      placeholder="Why members should pick you" value={statement}
                      onChange={(e) => setStatement(e.target.value)} />
                    <div className="flex gap-2">
                      <Button data-testid={`stand-confirm-${seat.key}`} size="sm"
                        onClick={() => stand(seat.key)} disabled={busy === `stand-${seat.key}`}>
                        {busy === `stand-${seat.key}` ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                        Put my name in
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setStanding("")}>Cancel</Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    {election.self_nominate && (
                      <Button data-testid={`stand-${seat.key}`} size="sm" variant="outline"
                        onClick={() => setStanding(seat.key)}>
                        Stand for this
                      </Button>
                    )}
                    <select data-testid={`nominate-pick-${seat.key}`} value={nominee[seat.key] || ""}
                      onChange={(e) => setNominee({ ...nominee, [seat.key]: e.target.value })}
                      className="h-9 rounded-md border border-ink-600 bg-ink-900 px-2 text-xs text-slate-200">
                      <option value="">Nominate a member…</option>
                      {roster.map((p) => (
                        <option key={p.user_id} value={p.user_id}>{p.name}</option>
                      ))}
                    </select>
                    {nominee[seat.key] && (
                      <Button data-testid={`nominate-send-${seat.key}`} size="sm"
                        onClick={() => nominate(seat.key)} disabled={busy === `nom-${seat.key}`}>
                        <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Nominate
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {votable && (
        <Button data-testid={`election-vote-${election.id}`} className="mt-4" onClick={vote}
          disabled={Object.keys(ballot).length === 0 || busy === "vote"}>
          {busy === "vote" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Cast my ballot
        </Button>
      )}
      {election.i_voted && (
        <p data-testid={`election-voted-${election.id}`} className="mt-4 text-xs text-emerald-300">
          Your ballot is in. Nobody — not even the leadership — can see how you voted.
        </p>
      )}
    </Panel>
  );
};
